/** @jsxImportSource theme-ui */
import classNames from 'classnames';
import React from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { ThemeUIStyleObject } from 'theme-ui';

import theme from 'theme';
import { isOpenLink as isKeyboardOpenLink } from 'utils/accessibilityHelpers';
import { locationWithinPath } from 'utils/routerUtils';

import { BdIcon } from 'components/_common/BdIcon';
import BdBetaPill from 'components/_common/BetaNotifications/BdBetaPill/BdBetaPill';
import { Typography } from 'components/_common/Typography';

import { NavigationbarUrl } from '../Navigationbar';

const Styles: ThemeUIStyleObject = {
  p: '32px 36px',
  width: '100%',
  '& .external-link-icon.icon.bd-icon': { verticalAlign: 'middle' },
  '&.compact': { p: '16px 32px' },
  '&.active': { bg: theme.colors.deepSea100 },
  '&:not(.top-level-nav-item)': { p: '10px 48px' },
};

type NavDrawerItemProps = {
  navItem: NavigationbarUrl;
  subItem?: boolean;
  small?: boolean;
  onClick: (link: string) => void;
};

const NavDrawerItem: React.FC<NavDrawerItemProps> = ({
  navItem,
  subItem = false,
  small = false,
  onClick,
}) => {
  const { pathname } = useLocation();

  const isActive = navItem.routerLink?.length && locationWithinPath(pathname, navItem.routerLink);

  const rootClasses = classNames({
    'drawer-item': true,
    compact: small,
    'top-level-nav-item': !subItem,
    active: isActive,
  });

  const handleSelectEvent = () => {
    navItem.onClick && navItem.onClick();
    onClick(navItem.routerLink);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement | HTMLAnchorElement>) =>
    isKeyboardOpenLink(e) && handleSelectEvent();

  const menuItem = (
    <>
      <Typography
        variant={small || subItem ? 'body2' : 'body1'}
        color="white"
        marginBottom="0"
        inline
      >
        {navItem.title}
        {navItem.isExternalLink && (
          <BdIcon className="external-link-icon" name="icon-direct-link" color="white" />
        )}
      </Typography>
      {navItem.isBeta && <BdBetaPill />}
    </>
  );

  return !navItem.routerLink ? (
    <div
      className={rootClasses}
      role="button"
      tabIndex={0}
      onClick={handleSelectEvent}
      onKeyPress={handleKeyPress}
      sx={Styles}
    >
      {menuItem}
    </div>
  ) : navItem.isExternalLink ? (
    <a
      className={rootClasses}
      href={navItem.routerLink}
      onClick={handleSelectEvent}
      onKeyPress={handleKeyPress}
      sx={Styles}
    >
      {menuItem}
    </a>
  ) : (
    <Link
      className={rootClasses}
      to={navItem.routerLink}
      onClick={handleSelectEvent}
      onKeyPress={handleKeyPress}
      sx={Styles}
    >
      {menuItem}
    </Link>
  );
};

export default NavDrawerItem;
